import React, { useRef, useState, useEffect } from "react";
import { Button, Box, IconButton } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";

export default function AudioRecorder({ value, onChange }: any) {
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null); // ✅ store mic stream

  // Sync with parent-provided audio
  useEffect(() => {
    if (value && value instanceof Blob) {
      setAudioURL(URL.createObjectURL(value));
    } else {
      setAudioURL(null);
    }
    return () => {
      if (audioURL) URL.revokeObjectURL(audioURL);
    };
    // eslint-disable-next-line
  }, [value]);

  // Start recording
  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream; // ✅ keep reference

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        setAudioURL(URL.createObjectURL(audioBlob));
        onChange?.(audioBlob);

        // ✅ stop microphone tracks after recording finishes
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      mediaRecorder.start();
      setRecording(true);
    } catch (err) {
      alert("Error accessing microphone!");
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  // Delete recording
  const deleteAudio = () => {
    setAudioURL(null);
    onChange?.(null);
  };

  return (
    <Box>
      <Button
        onClick={startRecording}
        variant="contained"
        color="primary"
        disabled={recording}
        startIcon={<MicIcon />}
        sx={{ mr: 2 }}
      >
        Aufnahme Starten
      </Button>

      <Button
        onClick={stopRecording}
        variant="outlined"
        color="error"
        disabled={!recording}
        startIcon={<StopIcon />}
        sx={{ mr: 2 }}
      >
        Aufnahme Stoppen
      </Button>

      {audioURL && (
        <Box display="flex" alignItems="center" gap={1} mt={2}>
          <audio
            src={audioURL}
            controls
            style={{ minWidth: 180, maxWidth: 300 }}
          />
          <IconButton
            onClick={deleteAudio}
            color="error"
            aria-label="Delete recording"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
